import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, Compass, Sparkles, Trophy, User, Bell, Sun, Moon, Library, Store, Wallet, Crown, Shield, Download, X, LogOut, Users } from "lucide-react";
import { useAuth, api } from "../lib/api";

export const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2 font-display font-black text-xl tracking-tight">
    <span className="grid place-items-center w-8 h-8 rounded-lg bg-[var(--jx-pink)] text-[#0A0C10] text-sm rotate-[-4deg]">JX</span>
    <span>Jixxeo<span className="jx-pink">.</span></span>
  </Link>
);

const MAIN = [["/", "Home", Home], ["/discover", "Discover", Compass], ["/create", "Create", Sparkles], ["/leaderboards", "Leaderboards", Trophy], ["/profile", "Profile", User]];
const MORE = [["/library", "Library", Library], ["/marketplace", "Marketplace", Store], ["/creators", "Creators", Users], ["/earnings", "Earnings", Wallet], ["/subscriptions", "Plans", Crown]];

export function useTheme() {
  const [theme, setTheme] = useState(localStorage.getItem("jx-theme") || "dark");
  useEffect(() => { document.documentElement.className = theme; localStorage.setItem("jx-theme", theme); }, [theme]);
  return [theme, () => setTheme(theme === "dark" ? "light" : "dark")];
}

export function TopBar() {
  const { user, logout } = useAuth();
  const [theme, toggle] = useTheme();
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  useEffect(() => { if (user) api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {}); }, [user]);
  return (
    <header className="sticky top-0 z-40 jx-glass border-b border-[var(--jx-border)]">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-6">
        <Logo />
        <nav className="hidden md:flex items-center gap-1 text-sm">
          {[...MAIN.slice(0, 4), ...MORE].map(([to, label]) => (
            <NavLink key={to} to={to} end data-testid={`nav-${label.toLowerCase()}`} className={({ isActive }) => `px-3 py-2 rounded-full transition-colors ${isActive ? "bg-[var(--jx-surface)] jx-pink font-semibold" : "jx-muted hover:text-[var(--jx-text)]"}`}>{label}</NavLink>
          ))}
          {user?.role === "admin" && <NavLink to="/admin" data-testid="nav-admin" className="px-3 py-2 rounded-full jx-gold flex items-center gap-1"><Shield size={14} />Admin</NavLink>}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="theme-toggle" onClick={toggle} className="w-10 h-10 grid place-items-center rounded-full jx-surface">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
          {user ? (<>
            <button data-testid="notifications-btn" onClick={() => nav("/notifications")} className="relative w-10 h-10 grid place-items-center rounded-full jx-surface">
              <Bell size={18} />{unread > 0 && <span data-testid="unread-count" className="absolute -top-1 -right-1 text-[10px] min-w-5 h-5 px-1 grid place-items-center rounded-full bg-[var(--jx-pink)] text-white">{unread}</span>}
            </button>
            <button data-testid="logout-btn" onClick={logout} className="hidden md:grid w-10 h-10 place-items-center rounded-full jx-surface"><LogOut size={16} /></button>
          </>) : user === false && <Link to="/login" data-testid="signin-link" className="jx-btn px-5 py-2 text-sm">Sign in</Link>}
        </div>
      </div>
      <div className="md:hidden flex gap-2 overflow-x-auto no-scrollbar px-4 pb-2">
        {[...MORE, ...(user?.role === "admin" ? [["/admin", "Admin", Shield]] : [])].map(([to, label, I]) => (
          <NavLink key={to} to={to} data-testid={`mnav-${label.toLowerCase()}`} className={({ isActive }) => `shrink-0 flex items-center gap-1 text-xs px-3 py-1.5 rounded-full jx-surface ${isActive ? "jx-pink" : "jx-muted"}`}><I size={12} />{label}</NavLink>
        ))}
      </div>
    </header>
  );
}

export function BottomNav() {
  return (
    <nav data-testid="bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 z-40 jx-glass border-t border-[var(--jx-border)] pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5 h-16">
        {MAIN.map(([to, label, I]) => label === "Create" ? (
          <NavLink key={to} to={to} data-testid="bottom-nav-create" className="grid place-items-center">
            <span className="-mt-7 w-14 h-14 rounded-2xl bg-[var(--jx-pink)] text-white grid place-items-center jx-ink rotate-[-6deg]"><I size={24} /></span>
          </NavLink>
        ) : (
          <NavLink key={to} to={to} end data-testid={`bottom-nav-${label.toLowerCase()}`} className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[10px] ${isActive ? "jx-pink" : "jx-muted"}`}><I size={20} />{label === "Leaderboards" ? "Ranks" : label}</NavLink>
        ))}
      </div>
    </nav>
  );
}

export function InstallBanner() {
  const [evt, setEvt] = useState(null);
  const [ios, setIos] = useState(false);
  const [hidden, setHidden] = useState(localStorage.getItem("jx-install-dismissed") === "1");
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) { setHidden(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    const onInstalled = () => { setHidden(true); localStorage.setItem("jx-install-dismissed", "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    setIos(/iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent));
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  if (hidden || (!evt && !ios)) return null;
  const dismiss = () => { setHidden(true); localStorage.setItem("jx-install-dismissed", "1"); };
  const install = async () => { evt.prompt(); const r = await evt.userChoice; if (r.outcome === "accepted") dismiss(); setEvt(null); };
  return (
    <div data-testid="install-banner" className="fixed z-50 left-3 right-3 bottom-20 md:bottom-6 md:left-auto md:right-6 md:w-96 jx-surface jx-ink rounded-2xl p-4 flex gap-3 items-center jx-rise">
      <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold">Install Jixxeo Comics</div>
        <div className="text-xs jx-muted">{ios ? "Tap Share, then “Add to Home Screen”." : "Read offline-ready, full screen. No ads, ever."}</div>
      </div>
      {!ios && <button data-testid="install-btn" onClick={install} className="jx-btn px-4 py-2 text-sm flex items-center gap-1"><Download size={14} />Install</button>}
      <button data-testid="install-dismiss-btn" onClick={dismiss} aria-label="Dismiss" className="jx-muted p-1"><X size={18} /></button>
    </div>
  );
}
